import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosget, axiospost } from "../../helpers/httprequest";
import PageTitle from "../../components/PageTitle";
import Loader from "../../components/Loader";
import ErrorResponse from "../../components/ErrorResponse";
import is_null from "../../helpers/is_null";
import swal from "../../helpers/swal";
import "./ChangeProgramme.css"; // New CSS file for styling

const ApprovalStatus = {
  PENDING: { value: 0, label: "Pending", color: "info" },
  HOD_APPROVED: { value: 1, label: "HOD Approved", color: "success" },
  MANAGEMENT_APPROVED: { value: 2, label: "Management Approved", color: "success" },
  REJECTED: { value: -1, label: "Rejected", color: "danger" }
};

const ChangeProgramme = () => {
  const [selectedProgramme, setSelectedProgramme] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["myProgrammeList"],
    queryFn: async () => {
      const response = await axiosget("ms_programme/change");
      return response?.data;
    },
  });

  const handleProgrammeChange = async () => {
    try {
      setIsSubmitting(true);
      
      if (is_null(selectedProgramme)) {
        throw new Error("Please select the programme you want to change to before submitting");
      }

      const response = await axiospost("ms_course_change", { to: selectedProgramme });
      
      if (response.status !== 200) {
        throw new Error("Sorry, something went wrong");
      }

      swal(response?.data?.type, response?.data?.message);
      
      if (response?.data?.type === "success") {
        queryClient.invalidateQueries({ queryKey: ["myProgrammeList"] });
      }
    } catch (error) {
      swal("error", error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusInfo = (statusValue) => {
    return Object.values(ApprovalStatus).find(
      status => status.value === Number.parseInt(statusValue)
    ) || ApprovalStatus.REJECTED;
  };

  if (isLoading) return <Loader />;
  if (error) return <ErrorResponse type="danger" message={`Error: ${error.message}`} />;
  if (data?.type !== undefined) return <ErrorResponse type="danger" message={data?.message} />;

  return (
    <div className="change-programme-container">
      <PageTitle 
        title="CHANGE OF PROGRAMME" 
        sub="Request to change your current programme" 
      />

      {/* Programme Change Form */}
      <div className="change-programme-form">
        <div className="form-row">
          <div className="form-group col-md-8">
            <label htmlFor="programme-select">Select New Programme</label>
            <select
              id="programme-select"
              className="form-control form-control-lg"
              onChange={(e) => setSelectedProgramme(e.target.value)}
              value={selectedProgramme || ""}
            >
              <option value="">Select programme...</option>
              {data?.check?.map((programme, index) => (
                <option key={`${programme._id}_${index}`} value={programme._id}>
                  {programme.code} - {programme.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group col-md-4 d-flex align-items-baseline">
            <button
              className="btn btn-primary btn-lg w-100"
              onClick={handleProgrammeChange}
              disabled={isSubmitting || !selectedProgramme}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                  Processing...
                </>
              ) : (
                "Apply for Change"
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Previous Requests */}
      <div className="previous-requests">
        {data?.changes?.length < 1 ? (
          <ErrorResponse 
            type="info" 
            title="NO CHANGE REQUESTS" 
            message="You haven't made any programme change requests yet" 
          />
        ) : (
          <div className="request-cards">
            {data?.changes?.map((request, index) => {
              const statusInfo = getStatusInfo(request?.approvalstatus);
              
              return (
                <div className="col-md-6 col-lg-6 my-3" key={`change_request_${index}`}>
                  <div className={`card request-card border-${statusInfo.color}`}>
                    <div className="card-header bg-transparent">
                      <h5 className="card-title mb-0">
                        Request #{index + 1}
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="request-detail">
                        <span className="detail-label">From Programme:</span>
                        <span className="detail-value">
                          {request?.frominfo?.code} - {request?.frominfo?.name}
                        </span>
                      </div>
                      <div className="request-detail">
                        <span className="detail-label">To Programme:</span>
                        <span className="detail-value">
                          {request?.toinfo?.code} - {request?.toinfo?.name}
                        </span>
                      </div>
                      <div className="request-detail">
                        <span className="detail-label">Level & Semester:</span>
                        <span className="detail-value">
                          {request?.fromlevel} | {request?.fromsemester === 1 ? "First" : "Second"} Semester
                        </span>
                      </div>
                      <div className="request-detail">
                        <span className="detail-label">Status:</span>
                        <span className={`badge badge-${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className="request-detail">
                        <span className="detail-label">Student ID:</span>
                        <span className="detail-value">
                          {request?.oldid} → {request?.newId}
                        </span>
                      </div>
                    </div>
                    <div className="card-footer bg-transparent">
                      <small className="text-muted">
                        Submitted on: {new Date(request?.createdAt).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChangeProgramme;